import React from "react";
import { Link } from "react-router-dom";
import { useSettings } from "@/lib/settings";
import { useSeo } from "@/lib/seo";
import { PageShell } from "@/pages/StaticPages";
import { UserPlus, ClipboardList, Upload, Clock, BadgeCheck, Search, ArrowRight, PlayCircle } from "@/lib/icons";

const STEPS = [
  {
    icon: UserPlus,
    title: "Create your free account",
    body: "Head to the Register page and sign up with your name, mobile number, email and a password. It takes under a minute.",
  },
  {
    icon: ClipboardList,
    title: "Complete your profile",
    body: "Add your category (photographer, videographer, editor and more), studio name, location and other professional details. A complete profile gets discovered faster.",
  },
  {
    icon: Upload,
    title: "Upload your photo",
    body: "A clear profile photo helps clients and fellow professionals recognize and trust your listing.",
  },
  {
    icon: Clock,
    title: "Submit for review",
    body: "Our team reviews every new registration to keep PRONET a genuine, professional community. This usually takes a short while.",
  },
  {
    icon: BadgeCheck,
    title: "Get your badge and Digital ID",
    body: "Once approved, you receive the PRONET badge and a shareable Digital ID Card with a unique QR code.",
  },
  {
    icon: Search,
    title: "Get discovered",
    body: "Your profile becomes visible on GoMore, PRONET's searchable directory, where clients and collaborators can find and contact you.",
  },
];

function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
}

export default function HowToRegister() {
  const { settings } = useSettings();
  const videos = (settings.how_to_register_videos || []).filter(Boolean);

  useSeo({
    title: "How to Register | PRONET",
    description: "Follow the steps to create your PRONET profile and receive your Pro ID and Digital ID Card.",
  });

  return (
    <PageShell>
      <div className="overline text-fuchsia-300">Get started</div>
      <h1 className="mt-3 font-display text-5xl md:text-6xl leading-none text-white" data-testid="how-to-register-title">
        How to <span className="italic text-gradient">register</span>
      </h1>
      <p className="mt-5 max-w-2xl text-white/60 leading-relaxed">
        Joining PRONET takes just a few steps. Here's exactly what happens from sign-up to going live on the directory.
      </p>

      <div className="mt-12 grid sm:grid-cols-2 gap-5 max-w-4xl">
        {STEPS.map((s, i) => (
          <div key={s.title} className="glass rounded-2xl p-6 fade-up" data-testid={`how-to-register-step-${i + 1}`}>
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-fuchsia-500/15 border border-fuchsia-500/25 text-fuchsia-200">
                <s.icon size={17} />
              </span>
              <div className="text-xs font-semibold text-white/40">STEP {i + 1}</div>
            </div>
            <div className="mt-4 font-display text-xl text-white">{s.title}</div>
            <p className="mt-2 text-sm text-white/60 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>

      {/* Video guides — links are managed from Admin > Settings */}
      <div className="mt-16 max-w-4xl">
        <div className="overline text-fuchsia-300">Watch and learn</div>
        <h2 className="mt-3 font-display text-3xl text-white">Video guides</h2>
        {videos.length > 0 ? (
          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            {videos.map((url, i) => {
              const videoId = extractYouTubeId(url);
              return (
                <div key={i} className="glass rounded-2xl overflow-hidden fade-up" data-testid={`how-to-register-video-${i + 1}`}>
                  {videoId ? (
                    <div className="aspect-video">
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title={`How to register on PRONET: video ${i + 1}`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <a href={url} target="_blank" rel="noreferrer"
                      className="flex items-center gap-3 p-6 text-white/70 hover:text-fuchsia-300">
                      <PlayCircle size={18} /> Watch video {i + 1}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mt-4 text-sm text-white/40">Video guides are coming soon.</p>
        )}
      </div>

      <Link to="/register" data-testid="how-to-register-cta"
        className="mt-14 inline-flex items-center gap-2 btn-glow rounded-full px-7 py-3.5 text-sm font-semibold text-white">
        Create your free account <ArrowRight size={15} />
      </Link>
    </PageShell>
  );
}
